import { type ReactNode } from 'react';
import { type FieldValues } from 'react-hook-form';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';

import { FormSkeleton } from '@/shared/components/feedback/Skeleton/Skeleton';

import { DynamicForm } from '../DynamicForm/DynamicForm';
import { type DynamicFormProps } from '../DynamicForm/DynamicForm.types';
import { flattenFields, resolveSections } from '../resolve-sections/resolve-sections';

/**
 * `Omit` over a union produces ONE object with every member's keys made
 * optional, which would collapse `{ fields } | { sections }` into a shape
 * satisfying neither. Distributing it keeps the two arms apart, so passing
 * `fields` still forbids `sections`.
 */
type DistributiveOmit<T, TKey extends PropertyKey> = T extends unknown ? Omit<T, TKey> : never;

/**
 * A type alias rather than an interface: `DynamicFormProps` is a UNION (a flat
 * field list or named sections), and an interface cannot extend one.
 *
 * **`renderLayout` is omitted, and that is a fix rather than a restriction.**
 * This component supplies its own — the one that hands the form's two regions to
 * `DialogBody` and `DialogFooter` — so a caller's was spread in and then silently
 * overwritten: no error, no effect, and a notice the caller believed they had
 * placed simply absent. Omitting it turns that into a compile error and points
 * the caller at `banner`, which is what they wanted.
 */
export type DynamicFormModalProps<TValues extends FieldValues> = DistributiveOmit<
  DynamicFormProps<TValues>,
  'renderLayout'
> & {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Caller-translated. */
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Rendered inside the scrolling body, **above** the fields.
   *
   * For the notice an operator has to read *before* they fill the form in — "this
   * replaces the comp underneath it", "the store will keep charging them". The
   * form's own `children` land under the fields, which is right for a refusal
   * (it explains the submit that just failed) and wrong for a warning (by then
   * the operator has already chosen).
   *
   * Not shown while `isLoading`: a warning derived from a record that has not
   * arrived would be a sentence about the wrong thing.
   */
  banner?: ReactNode;
  /**
   * The record being edited has not arrived yet.
   *
   * The form is NOT rendered while this is true, and that is the point: React
   * Hook Form reads `defaultValues` once, at mount, so a form mounted against an
   * empty record stays empty forever when the real one lands a moment later —
   * the operator opens Edit, the fields are blank, and saving wipes the record.
   * Holding the mount until the data is here is the fix; a skeleton keeps the
   * dialog from looking broken while it waits.
   */
  isLoading?: boolean;
};

/**
 * A `DynamicForm` in a dialog.
 *
 * The dialog is **not** dismissed by the form: `onSubmit` resolving is the
 * caller's signal to close, because only the caller knows whether the mutation
 * really landed and whether a second step follows. A modal that closed itself
 * on submit would hide the server error the form is about to display.
 *
 * `Cancel` is wired to `onOpenChange(false)` rather than to a separate handler
 * so the button, the Escape key and the overlay click all take the same path —
 * which is also the one place `useUnsavedChangesGuard`'s `confirmDiscard()`
 * would be awaited if this modal ever guarded its own edits (P10.3d built the
 * hook for the settings screen; nothing has wired it in here yet).
 *
 * The fields scroll and the actions do not. `DynamicForm` used to render its
 * action row as the last child of the `<form>`, so on a tall form (the role
 * editor's ~2500px permission checklist) Save scrolled away with the fields —
 * and, before the dialog was clamped at all, straight off the bottom of the
 * viewport. `renderLayout` exists so the form can hand its two regions to
 * `DialogBody` and `DialogFooter` while keeping the `<form>` element around both:
 * wrapping them separately would put the submit button outside its own form.
 */
export function DynamicFormModal<TValues extends FieldValues>({
  isOpen,
  onOpenChange,
  title,
  description,
  size = 'md',
  isLoading = false,
  banner,
  onCancel,
  ...formProps
}: DynamicFormModalProps<TValues>) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent size={size}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description === undefined ? null : <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {isLoading ? (
          <DialogBody>
            {/* Counted from the flattened layout: a sectioned form has no
                `fields` prop to count, and the skeleton should still show as
                many rows as the form will. */}
            <FormSkeleton fields={flattenFields(resolveSections(formProps)).length} />
          </DialogBody>
        ) : (
          <DynamicForm
            {...formProps}
            // The form becomes the dialog's middle row: it must be allowed to
            // shrink (`min-h-0`) and to take the leftover height (`flex-1`), or
            // the body inside it never gets a bounded box to scroll in.
            className="min-h-0 flex-1 gap-lg"
            onCancel={
              onCancel ??
              (() => {
                onOpenChange(false);
              })
            }
            renderLayout={({ fields, actions }) => (
              <>
                <DialogBody>
                  {banner === undefined ? null : (
                    // Inside the body rather than the header, so a long warning
                    // scrolls with the fields instead of eating the dialog's
                    // fixed height and pushing Save out of reach.
                    <div className="pb-lg">{banner}</div>
                  )}
                  {fields}
                </DialogBody>
                <DialogFooter>{actions}</DialogFooter>
              </>
            )}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
